/**
 * TradingViewChart.jsx
 * High-performance, interactive financial charting component powered by TradingView Lightweight Charts (v5).
 * Supports Candlestick, Area, Line views, volume histogram, multi-timeframe switching,
 * hover HUD legend, user's average purchase cost line, and BUY/SELL trade execution markers.
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  createChart,
  CandlestickSeries,
  AreaSeries,
  LineSeries,
  HistogramSeries,
  ColorType,
  LineStyle,
  CrosshairMode,
  createSeriesMarkers
} from 'lightweight-charts';
import * as apiClient from '../services/apiClient';
import ThinkingOrbLoader from './ThinkingOrbLoader';

const TIMEFRAMES = [
  { label: '1D', range: '1d', interval: '5m' },
  { label: '5D', range: '5d', interval: '15m' },
  { label: '1M', range: '1mo', interval: '1d' },
  { label: '3M', range: '3mo', interval: '1d' },
  { label: '6M', range: '6mo', interval: '1d' },
  { label: '1Y', range: '1y', interval: '1d' },
  { label: '5Y', range: '5y', interval: '1wk' },
  { label: 'ALL', range: 'max', interval: '1mo' }
];

export default function TradingViewChart({
  symbol,
  name,
  currency = 'USD',
  avgCost = 0,
  userTransactions = [],
  height = 360,
  showControls = true,
  isModal = false,
  onClose = null
}) {
  const chartContainerRef = useRef(null);
  const chartInstanceRef = useRef(null);
  const mainSeriesRef = useRef(null);
  const volumeSeriesRef = useRef(null);
  const markersRef = useRef(null);
  const priceLineRef = useRef(null);

  const [activeTimeframe, setActiveTimeframe] = useState('1M');
  const [chartType, setChartType] = useState('candlestick'); // 'candlestick' | 'area' | 'line'
  const [showVolume, setShowVolume] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [chartData, setChartData] = useState(null);

  // Hover Crosshair HUD state
  const [hoverData, setHoverData] = useState(null);

  // Fetch chart data when symbol or timeframe changes
  const fetchHistory = useCallback(async () => {
    if (!symbol) return;
    setLoading(true);
    setError(null);

    const tf = TIMEFRAMES.find(t => t.label === activeTimeframe) || TIMEFRAMES[2];

    try {
      const data = await apiClient.request(
        `/market/history?symbol=${encodeURIComponent(symbol)}&range=${tf.range}&interval=${tf.interval}`
      );
      setChartData(data);
    } catch (err) {
      console.error(`Error fetching chart history for ${symbol}:`, err);
      setError(err.message || 'Failed to load chart data');
    } finally {
      setLoading(false);
    }
  }, [symbol, activeTimeframe]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Initialize and update Lightweight Chart
  useEffect(() => {
    if (!chartContainerRef.current || !chartData || loading) return;

    // Clean up existing chart instance before creating a new one
    if (chartInstanceRef.current) {
      try {
        chartInstanceRef.current.remove();
      } catch (e) {}
      chartInstanceRef.current = null;
      mainSeriesRef.current = null;
      volumeSeriesRef.current = null;
    }

    const container = chartContainerRef.current;
    const containerWidth = container.clientWidth || 600;

    // Create TradingView Chart instance with tezer Dark Theme
    const chart = createChart(container, {
      width: containerWidth,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: 'transparent' },
        textColor: '#94a3b8',
        fontSize: 11,
        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      },
      grid: {
        vertLines: { color: 'rgba(255, 255, 255, 0.04)' },
        horzLines: { color: 'rgba(255, 255, 255, 0.04)' }
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: 'rgba(0, 245, 212, 0.4)',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#0f172a'
        },
        horzLine: {
          color: 'rgba(0, 245, 212, 0.4)',
          width: 1,
          style: LineStyle.Dashed,
          labelBackgroundColor: '#0f172a'
        }
      },
      rightPriceScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        scaleMargins: {
          top: 0.1,
          bottom: showVolume ? 0.22 : 0.08
        }
      },
      timeScale: {
        borderColor: 'rgba(255, 255, 255, 0.08)',
        timeVisible: activeTimeframe === '1D' || activeTimeframe === '5D',
        secondsVisible: false
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: true
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: false
      }
    });

    chartInstanceRef.current = chart;

    // Add Volume Series if enabled
    if (showVolume && chartData.volumeData && chartData.volumeData.length > 0) {
      const volSeries = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
        scaleMargins: {
          top: 0.78,
          bottom: 0
        }
      });
      chart.priceScale('volume').applyOptions({
        scaleMargins: {
          top: 0.8,
          bottom: 0
        }
      });
      volSeries.setData(chartData.volumeData);
      volumeSeriesRef.current = volSeries;
    }

    // Add Main Price Series (Candlestick, Area, or Line)
    let mainSeries;
    if (chartType === 'candlestick' && chartData.candles && chartData.candles.length > 0) {
      mainSeries = chart.addSeries(CandlestickSeries, {
        upColor: '#00f5d4',
        downColor: '#ff4757',
        borderVisible: false,
        wickUpColor: '#00f5d4',
        wickDownColor: '#ff4757'
      });
      mainSeries.setData(chartData.candles);
    } else if (chartType === 'area') {
      mainSeries = chart.addSeries(AreaSeries, {
        lineColor: '#00f5d4',
        topColor: 'rgba(0, 245, 212, 0.32)',
        bottomColor: 'rgba(0, 245, 212, 0.01)',
        lineWidth: 2
      });
      mainSeries.setData(chartData.lineData || []);
    } else {
      mainSeries = chart.addSeries(LineSeries, {
        color: '#00f5d4',
        lineWidth: 2,
        crosshairMarkerVisible: true,
        crosshairMarkerRadius: 5,
        crosshairMarkerBackgroundColor: '#00f5d4'
      });
      mainSeries.setData(chartData.lineData || []);
    }

    mainSeriesRef.current = mainSeries;

    // Overlay Average Cost Line if user holds units
    if (avgCost > 0) {
      const avgCostNum = parseFloat(avgCost);
      const priceLine = mainSeries.createPriceLine({
        price: avgCostNum,
        color: '#ff9f43',
        lineWidth: 2,
        lineStyle: LineStyle.Dotted,
        axisLabelVisible: true,
        title: `Avg Cost: ${avgCostNum.toFixed(2)}`
      });
      priceLineRef.current = priceLine;
    }

    // Overlay Historical Buy/Sell Markers
    if (userTransactions && userTransactions.length > 0 && chartData.candles && chartData.candles.length > 0) {
      const availableDates = new Set(chartData.candles.map(c => typeof c.time === 'string' ? c.time : c.time));
      const markers = [];

      userTransactions.forEach(tx => {
        if (!tx.transaction_date || (tx.type !== 'BUY' && tx.type !== 'SELL')) return;
        const txDateStr = tx.transaction_date.split('T')[0];

        // Find matching date in chart or nearest available
        const markerTime = txDateStr;

        if (tx.type === 'BUY') {
          markers.push({
            time: markerTime,
            position: 'belowBar',
            color: '#00f5d4',
            shape: 'arrowUp',
            text: `BUY ${parseFloat(tx.units || 0).toFixed(1)} @ ${parseFloat(tx.price_per_unit || 0).toFixed(2)}`,
            size: 1
          });
        } else if (tx.type === 'SELL') {
          markers.push({
            time: markerTime,
            position: 'aboveBar',
            color: '#ff4757',
            shape: 'arrowDown',
            text: `SELL ${parseFloat(tx.units || 0).toFixed(1)} @ ${parseFloat(tx.price_per_unit || 0).toFixed(2)}`,
            size: 1
          });
        }
      });

      if (markers.length > 0) {
        // Sort markers by time
        markers.sort((a, b) => (a.time > b.time ? 1 : -1));
        try {
          if (typeof createSeriesMarkers === 'function') {
            markersRef.current = createSeriesMarkers(mainSeries, markers);
          } else if (typeof mainSeries.setMarkers === 'function') {
            mainSeries.setMarkers(markers);
          }
        } catch (mErr) {
          console.warn('Could not render trade markers:', mErr);
        }
      }
    }

    // Subscribe to Crosshair Move for Live HUD Tooltip
    chart.subscribeCrosshairMove(param => {
      if (!param || !param.time || !param.seriesData || param.point === undefined) {
        setHoverData(null);
        return;
      }

      const mainData = param.seriesData.get(mainSeries);
      const volData = volumeSeriesRef.current ? param.seriesData.get(volumeSeriesRef.current) : null;

      if (mainData) {
        const timeStr = typeof param.time === 'string' 
          ? param.time 
          : new Date(param.time * 1000).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

        setHoverData({
          time: timeStr,
          open: mainData.open !== undefined ? mainData.open : null,
          high: mainData.high !== undefined ? mainData.high : null,
          low: mainData.low !== undefined ? mainData.low : null,
          close: mainData.close !== undefined ? mainData.close : (mainData.value !== undefined ? mainData.value : null),
          volume: volData?.value || null
        });
      } else {
        setHoverData(null);
      }
    });

    // Auto-fit content
    chart.timeScale().fitContent();

    // ResizeObserver for fluid responsiveness
    const resizeObserver = new ResizeObserver(entries => {
      if (entries.length === 0 || !entries[0].target) return;
      const newRect = entries[0].contentRect;
      if (newRect.width > 0) {
        chart.applyOptions({ width: newRect.width });
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      if (chartInstanceRef.current) {
        chartInstanceRef.current.remove();
        chartInstanceRef.current = null;
      }
    };
  }, [chartData, chartType, showVolume, height, activeTimeframe, avgCost, userTransactions, loading]);

  // Current or Latest price metrics
  const latestPrice = chartData?.currentPrice || (chartData?.candles?.[chartData.candles.length - 1]?.close) || 0;
  const prevClose = chartData?.previousClose || latestPrice;
  const priceDiff = latestPrice - prevClose;
  const pricePct = prevClose > 0 ? (priceDiff / prevClose) * 100 : 0;
  const isPositive = priceDiff >= 0;

  return (
    <div
      className={isModal ? 'modal-backdrop modal-backdrop-blur' : ''}
      style={isModal ? { zIndex: 1000 } : { width: '100%' }}
    >
      <div
        className="glass-panel"
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: isModal ? '20px' : '16px',
          maxWidth: isModal ? '900px' : '100%',
          margin: isModal ? 'auto' : '0',
          position: 'relative',
          borderRadius: '16px',
          background: 'rgba(15, 23, 42, 0.75)',
          border: '1px solid rgba(255, 255, 255, 0.08)',
          boxShadow: '0 20px 40px rgba(0,0,0,0.5)'
        }}
      >
        {/* Header: Asset Info, Live Price, and Modal Actions */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '10px', marginBottom: '14px' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text-main)', letterSpacing: '-0.02em' }}>
                {symbol}
              </span>
              <span style={{ fontSize: '10px', background: 'rgba(0, 245, 212, 0.12)', color: 'var(--accent-cyan)', padding: '2px 6px', borderRadius: '6px', fontWeight: '700' }}>
                {currency}
              </span>
              {avgCost > 0 && (
                <span style={{ fontSize: '10px', background: 'rgba(255, 159, 67, 0.15)', color: '#ff9f43', padding: '2px 6px', borderRadius: '6px', fontWeight: '600' }}>
                  Avg Buy: {currency} {parseFloat(avgCost).toFixed(2)}
                </span>
              )}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
              {name || chartData?.meta?.shortName || symbol} • {chartData?.meta?.exchangeName || 'Market'}
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '20px', fontWeight: '800', color: 'var(--text-main)' }}>
                {latestPrice ? `${currency} ${latestPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}` : '—'}
              </div>
              <div style={{ fontSize: '11px', fontWeight: '700', color: isPositive ? 'var(--color-inflow)' : 'var(--color-outflow)', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                <span>{isPositive ? '▲ +' : '▼ '}{priceDiff.toFixed(2)}</span>
                <span>({pricePct > 0 ? '+' : ''}{pricePct.toFixed(2)}%)</span>
              </div>
            </div>

            {isModal && onClose && (
              <button
                type="button"
                onClick={onClose}
                style={{
                  background: 'rgba(255, 255, 255, 0.06)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  borderRadius: '50%',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  transition: 'all 0.2s'
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Chart Toolbar Controls (Timeframes, Chart Type, Volume) */}
        {showControls && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px', paddingBottom: '10px', borderBottom: '1px solid rgba(255,255,255,0.05)', marginBottom: '10px' }}>
            {/* Timeframe Buttons */}
            <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '3px', border: '1px solid rgba(255,255,255,0.05)', gap: '2px' }}>
              {TIMEFRAMES.map(tf => (
                <button
                  key={tf.label}
                  type="button"
                  onClick={() => setActiveTimeframe(tf.label)}
                  style={{
                    background: activeTimeframe === tf.label ? 'var(--accent-cyan)' : 'transparent',
                    color: activeTimeframe === tf.label ? '#000' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: '7px',
                    padding: '3px 8px',
                    fontSize: '10px',
                    fontWeight: '700',
                    cursor: 'pointer',
                    transition: 'all 0.15s'
                  }}
                >
                  {tf.label}
                </button>
              ))}
            </div>

            {/* Chart Type Switcher & Volume Toggle */}
            <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
              <div style={{ display: 'flex', background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '3px', border: '1px solid rgba(255,255,255,0.05)', gap: '2px' }}>
                <button
                  type="button"
                  title="Candlestick Chart"
                  onClick={() => setChartType('candlestick')}
                  style={{
                    background: chartType === 'candlestick' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: chartType === 'candlestick' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: '7px',
                    padding: '3px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  🕯️ Candles
                </button>
                <button
                  type="button"
                  title="Area Chart"
                  onClick={() => setChartType('area')}
                  style={{
                    background: chartType === 'area' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: chartType === 'area' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: '7px',
                    padding: '3px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  📈 Area
                </button>
                <button
                  type="button"
                  title="Line Chart"
                  onClick={() => setChartType('line')}
                  style={{
                    background: chartType === 'line' ? 'rgba(255,255,255,0.1)' : 'transparent',
                    color: chartType === 'line' ? 'var(--accent-cyan)' : 'var(--text-muted)',
                    border: 'none',
                    borderRadius: '7px',
                    padding: '3px 8px',
                    fontSize: '11px',
                    cursor: 'pointer',
                    fontWeight: '600'
                  }}
                >
                  📉 Line
                </button>
              </div>

              <button
                type="button"
                onClick={() => setShowVolume(!showVolume)}
                style={{
                  background: showVolume ? 'rgba(0, 245, 212, 0.12)' : 'rgba(255,255,255,0.03)',
                  color: showVolume ? 'var(--accent-cyan)' : 'var(--text-muted)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  borderRadius: '10px',
                  padding: '4px 8px',
                  fontSize: '10px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                📊 Vol
              </button>
            </div>
          </div>
        )}

        {/* Live Hover HUD Legend */}
        {hoverData && (
          <div
            style={{
              display: 'flex',
              gap: '12px',
              fontSize: '10px',
              color: 'var(--text-muted)',
              marginBottom: '6px',
              flexWrap: 'wrap',
              background: 'rgba(0,0,0,0.25)',
              padding: '4px 8px',
              borderRadius: '6px',
              border: '1px solid rgba(255,255,255,0.04)'
            }}
          >
            <span>📅 <strong>{hoverData.time}</strong></span>
            {hoverData.open !== null && <span>O: <strong style={{ color: 'var(--text-main)' }}>{hoverData.open?.toFixed(2)}</strong></span>}
            {hoverData.high !== null && <span>H: <strong style={{ color: 'var(--color-inflow)' }}>{hoverData.high?.toFixed(2)}</strong></span>}
            {hoverData.low !== null && <span>L: <strong style={{ color: 'var(--color-outflow)' }}>{hoverData.low?.toFixed(2)}</strong></span>}
            {hoverData.close !== null && <span>C: <strong style={{ color: 'var(--accent-cyan)' }}>{hoverData.close?.toFixed(2)}</strong></span>}
            {hoverData.volume !== null && <span>Vol: <strong style={{ color: 'var(--text-main)' }}>{Number(hoverData.volume).toLocaleString()}</strong></span>}
          </div>
        )}

        {/* Chart Canvas or Loading/Error State */}
        {loading ? (
          <div
            style={{
              height: `${height}px`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              color: 'var(--text-muted)',
              background: 'rgba(0,0,0,0.2)',
              borderRadius: '12px'
            }}
          >
            <ThinkingOrbLoader state="searching" size={32} color="var(--accent-cyan)" />
            <span style={{ fontSize: '11px', fontWeight: '600' }}>Loading TradingView Chart for {symbol}...</span>
          </div>
        ) : error ? (
          <div
            style={{
              height: `${height}px`,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '10px',
              color: 'var(--color-outflow)',
              background: 'rgba(255, 71, 87, 0.05)',
              borderRadius: '12px',
              padding: '20px',
              textAlign: 'center'
            }}
          >
            <span style={{ fontSize: '24px' }}>⚠️</span>
            <div style={{ fontSize: '12px', fontWeight: '600' }}>{error}</div>
            <button
              type="button"
              onClick={fetchHistory}
              className="btn-secondary"
              style={{ padding: '4px 12px', fontSize: '11px' }}
            >
              🔄 Retry
            </button>
          </div>
        ) : (
          <div
            ref={chartContainerRef}
            style={{
              width: '100%',
              height: `${height}px`,
              borderRadius: '8px',
              overflow: 'hidden'
            }}
          />
        )}

        {/* Footer Note */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', fontSize: '9px', color: 'var(--text-muted)' }}>
          <span>TradingView Lightweight Charts™ • Canvas 60fps</span>
          <span>⚡ Live Market Stream (Yahoo Finance)</span>
        </div>
      </div>
    </div>
  );
}
